import React, { useMemo, useState } from 'react';
import { useQuery, gql } from '@apollo/client';
import './actividad_admins.css';

// Pestaña "Actividad" (superadmin): que hacen los admins de NAWI Office. Desde el 26/10/2026 reparte los movimientos
// en pestañas internas (Warnings, Por admin, Altas, Registro) y los agrupa por día en acordeones.
const GET_ACTIVIDAD = gql`
  query GetActividadAdmins($dias: Int) {
    getActividadAdmins(dias: $dias) {
      error errorMensaje
      resumen { dias desde registroDesde altasCuentas altasAdmins personasAgregadas logins loginsFallidos cambiosRol warnings }
      admins { email username rol activo cuentas personas logins loginsFallidos ultimoLogin ultimaActividad cambiosRol eventos }
      warnings { fecha actor rolActor descripcion objetivo detalle fuente }
      ultimasAltas { tipo email empresa creadoPor fecha personas conRegistro pasos { id fecha descripcion objetivo cantidad ok error } }
    }
  }
`;
const GET_REGISTRO = gql`
  query GetRegistroActividad($pagina: Int, $porPagina: Int, $actor: String, $categoria: String) {
    getRegistroActividad(pagina: $pagina, porPagina: $porPagina, actor: $actor, categoria: $categoria) {
      total pagina paginas porPagina
      items { id fecha origen operacion categoria descripcion objetivo cantidad detalle ok error ip actor { email username rol } }
    }
  }
`;

const TZ = 'America/Argentina/Buenos_Aires';
const PERIODOS = [[7, '7 días'], [30, '30 días'], [90, '90 días'], [365, '1 año']];
const CATEGORIAS = [
  ['', 'Todas las acciones'], ['login', 'Inicios de sesión'], ['alta_cuenta', 'Altas de cuentas'], ['alta_admin', 'Altas de usuarios de Office'],
  ['rol', 'Roles y accesos'], ['personas', 'Personas agregadas'], ['personas_baja', 'Personas quitadas'], ['baja', 'Bajas y bloqueos'], ['consumo', 'Wallets y consumo'],
  ['reactivacion', 'Reactivaciones'], ['password', 'Contraseñas'], ['credenciales', 'Credenciales de preproducción'],
  ['consulta', 'Consultas de datos sensibles'], ['agentes', 'Agentes y sus mails'], ['soporte', 'Buzón de soporte'], ['otro', 'Otras'],
];
const NOMBRE_CATEGORIA = Object.fromEntries(CATEGORIAS.filter(([v]) => v));
const ROL_TAG = { superadmin: 'tag--purple', admin: 'tag--blue', user: 'tag--green' };
const POR_PAGINA = 20;
const COLORES = ['#4f6ef7', '#18794e', '#a07516', '#6d28d9', '#0e7490', '#c2410c', '#1a56db', '#be185d'];

const fecha = (iso) => (iso ? new Date(iso).toLocaleString('es-AR', { timeZone: TZ, day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');
const soloDia = (iso) => (iso ? new Date(iso).toLocaleDateString('es-AR', { timeZone: TZ, day: '2-digit', month: '2-digit', year: 'numeric' }) : '');
const hora = (iso) => (iso ? new Date(iso).toLocaleTimeString('es-AR', { timeZone: TZ, hour: '2-digit', minute: '2-digit', hour12: false }) : '');
const num = (v) => (v === null || v === undefined ? '—' : new Intl.NumberFormat('es-AR').format(v));
function haceCuanto(iso) {
  if (!iso) return null;
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  const n = (v, uno, varios) => `hace ${v} ${v === 1 ? uno : varios}`;
  if (s < 60) return 'hace instantes';
  if (s < 3600) return n(Math.floor(s / 60), 'minuto', 'minutos');
  if (s < 86400) return n(Math.floor(s / 3600), 'hora', 'horas');
  if (s < 7 * 86400) return n(Math.floor(s / 86400), 'día', 'días');
  if (s < 30 * 86400) return n(Math.floor(s / (7 * 86400)), 'semana', 'semanas');
  if (s < 365 * 86400) return n(Math.floor(s / (30 * 86400)), 'mes', 'meses');
  return n(Math.floor(s / (365 * 86400)), 'año', 'años');
}
// "Hoy", "Ayer" o la fecha, en hora de Argentina
function nombreDia(iso) {
  const clave = (d) => d.toLocaleDateString('en-CA', { timeZone: TZ });
  const d = clave(new Date(iso));
  if (d === clave(new Date())) return 'Hoy';
  if (d === clave(new Date(Date.now() - 864e5))) return 'Ayer';
  return new Date(iso).toLocaleDateString('es-AR', { timeZone: TZ, weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}
function porDia(items, campo = 'fecha') {
  const grupos = [];
  for (const it of items) {
    const nombre = it[campo] ? nombreDia(it[campo]) : 'Sin fecha';
    const g = grupos[grupos.length - 1];
    if (g && g.nombre === nombre) g.items.push(it); else grupos.push({ nombre, items: [it] });
  }
  return grupos;
}
const Rol = ({ rol }) => (rol ? <span className={`tag ${ROL_TAG[rol] || 'tag--gray'}`}>{rol}</span> : null);
const colorDe = (t) => COLORES[[...String(t || '')].reduce((a, c) => (a * 31 + c.charCodeAt(0)) >>> 0, 7) % COLORES.length];
function Avatar({ texto }) {
  const t = String(texto || '').trim();
  return <span className={`aa-avatar ${t ? '' : 'aa-avatar--vacio'}`} style={t ? { background: colorDe(t) } : null} aria-hidden="true">{t ? t.charAt(0).toUpperCase() : '?'}</span>;
}
function Quien({ email, rol, vacio = 'Sin identificar' }) {
  return (
    <span className="aa-quien">
      <Avatar texto={email} />
      <span className="aa-quien-txt"><span className="aa-quien-email" title={email || ''}>{email || vacio}</span><Rol rol={rol} /></span>
    </span>
  );
}
function detallePares(json) {
  if (!json) return [];
  try {
    return Object.entries(JSON.parse(json)).filter(([, v]) => v !== null && v !== '' && typeof v !== 'object');
  } catch { return []; }
}

function Kpi({ titulo, valor, pie, alerta, onClick }) {
  return (
    <button type="button" className={`aa-kpi ${alerta ? 'aa-kpi--alerta' : ''}`} onClick={onClick}>
      <span className="aa-kpi-titulo">{titulo}</span>
      <b className="aa-kpi-valor">{valor}</b>
      {pie && <span className="aa-kpi-pie">{pie}</span>}
    </button>
  );
}

// Acordeón de un día (o de lo que sea): título, cantidad y contenido
function Grupo({ titulo, cantidad, abierto, children, tono }) {
  return (
    <details className={`aa-grupo ${tono ? `aa-grupo--${tono}` : ''}`} open={abierto || undefined}>
      <summary className="aa-grupo-cabeza">
        <span className="aa-chevron" aria-hidden="true" />
        <span className="aa-grupo-titulo">{titulo}</span>
        {cantidad != null && <span className="aa-grupo-n">{num(cantidad)}</span>}
      </summary>
      <div className="aa-grupo-cuerpo">{children}</div>
    </details>
  );
}

function Warnings({ lista }) {
  const grupos = useMemo(() => porDia(lista), [lista]);
  if (!lista.length) return <div className="aa-vacio">Sin altas ni cambios de rol en el período.</div>;
  return (
    <div className="aa-grupos">
      <p className="aa-ayuda">Altas de cuentas, usuarios de Office y cambios de rol hechos por admins y superadmins. Revisalos: son los movimientos que dan acceso.</p>
      {grupos.map((g, i) => (
        <Grupo key={g.nombre} titulo={g.nombre} cantidad={g.items.length} abierto={i === 0} tono="alerta">
          {g.items.map((w, j) => (
            <div key={`${w.fecha}-${j}`} className="aa-mov">
              <span className="aa-mov-hora">{hora(w.fecha)}</span>
              <Quien email={w.actor} rol={w.rolActor} />
              <span className="aa-mov-que"><b>{w.descripcion}</b>{w.objetivo ? <span className="aa-mov-sobre">{w.objetivo}</span> : null}{w.detalle ? <span className="aa-mov-sobre">{w.detalle}</span> : null}</span>
              <span className={`tag ${w.fuente === 'registro' ? 'tag--gray' : 'tag--yellow'}`} title={w.fuente === 'registro' ? 'Del registro de actividad' : 'Reconstruido desde las cuentas'}>{w.fuente === 'registro' ? 'registro' : 'cuentas'}</span>
              <span className="aa-chevron aa-chevron--vacio" aria-hidden="true" />
            </div>
          ))}
        </Grupo>
      ))}
    </div>
  );
}

function PorAdmin({ admins, sinRegistro }) {
  if (!admins.length) return <div className="aa-vacio">Sin admins con actividad.</div>;
  return (
    <div className="aa-admins">
      {admins.map((a) => (
        <div key={a.email} className={`aa-admin ${a.activo ? '' : 'aa-admin--inactivo'}`}>
          <div className="aa-admin-cabeza">
            <Quien email={a.email} rol={a.rol} />
            {!a.activo && <span className="tag tag--gray">inactivo</span>}
          </div>
          <div className="aa-admin-datos">
            <div><b>{num(a.cuentas)}</b><span>Cuentas</span></div>
            <div><b>{num(a.personas)}</b><span>Personas</span></div>
            <div><b>{sinRegistro ? '—' : num(a.logins)}</b><span>Inicios{a.loginsFallidos > 0 ? <em className="aa-fallidos"> · {a.loginsFallidos} fallido{a.loginsFallidos === 1 ? '' : 's'}</em> : null}</span></div>
            <div><b>{sinRegistro ? '—' : num(a.cambiosRol)}</b><span>Roles</span></div>
          </div>
          <div className="aa-admin-pie">
            <span title={fecha(a.ultimoLogin)}>Último inicio: <b>{a.ultimoLogin ? haceCuanto(a.ultimoLogin) : (sinRegistro ? '—' : 'sin inicios')}</b></span>
            <span title={fecha(a.ultimaActividad)}>Última actividad: <b>{a.ultimaActividad ? haceCuanto(a.ultimaActividad) : '—'}</b></span>
          </div>
        </div>
      ))}
    </div>
  );
}

function Altas({ lista }) {
  if (!lista.length) return <div className="aa-vacio">Todavía no hay altas.</div>;
  return (
    <div className="aa-grupos">
      <p className="aa-ayuda">Los últimos 10 usuarios dados de alta y los pasos que se registraron en cada alta.</p>
      {lista.map((a, i) => (
        <details key={`${a.email}-${i}`} className="aa-grupo aa-alta" open={i === 0 || undefined}>
          <summary className="aa-grupo-cabeza aa-alta-cabeza">
            <span className="aa-chevron" aria-hidden="true" />
            <Avatar texto={a.email} />
            <span className="aa-alta-titulo">
              <span className="aa-alta-linea"><b className="aa-alta-email">{a.email}</b><span className="tag tag--blue">{a.tipo}</span></span>
              <span className="aa-alta-sub">{a.empresa ? `${a.empresa} · ` : ''}{haceCuanto(a.fecha)}</span>
            </span>
            {a.pasos.length > 0 && <span className="aa-grupo-n" title="Pasos registrados">{a.pasos.length}</span>}
          </summary>
          <div className="aa-grupo-cuerpo">
            <div className="aa-alta-datos">
              <span>Dada de alta por <b>{a.creadoPor || 'sin registro'}</b></span>
              <span>{fecha(a.fecha)}</span>
              {a.personas !== null && <span><b>{num(a.personas)}</b> personas activas en su panel</span>}
            </div>
            {a.pasos.length > 0 ? (
              <ol className="aa-pasos">
                {a.pasos.map((p) => (
                  <li key={p.id} className={p.ok ? '' : 'aa-paso--error'}>
                    <span className="aa-paso-hora">{hora(p.fecha)}</span>
                    <span>{p.descripcion}{p.objetivo ? `: ${p.objetivo}` : ''}{Number.isInteger(p.cantidad) ? ` · ${p.cantidad} ${p.cantidad === 1 ? 'persona' : 'personas'}` : ''}{p.ok ? '' : ` · falló: ${p.error || ''}`}</span>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="aa-tenue aa-sin-pasos">{a.conRegistro ? 'Sin pasos registrados para esta alta.' : 'Sin pasos: el alta es anterior al registro de actividad.'}</p>
            )}
          </div>
        </details>
      ))}
    </div>
  );
}

function Movimiento({ e }) {
  const pares = detallePares(e.detalle);
  const personas = Number.isInteger(e.cantidad) ? `${e.cantidad} ${e.cantidad === 1 ? 'persona' : 'personas'}` : null;
  const hayDetalle = pares.length > 0 || personas || e.ip || (!e.ok && e.error);
  const fila = (
    <>
      <span className="aa-mov-hora">{hora(e.fecha)}</span>
      {e.actor ? <Quien email={e.actor.email || e.actor.username} rol={e.actor.rol} /> : <Quien email="" vacio="Sin sesión" />}
      <span className="aa-mov-que">
        <b>{e.descripcion}</b>
        {e.objetivo && <span className="aa-mov-sobre">{e.objetivo}</span>}
        <span className="aa-mov-cat">{NOMBRE_CATEGORIA[e.categoria] || e.categoria}</span>
      </span>
      {e.ok ? <span className="tag tag--green">ok</span> : <span className="tag tag--red" title={e.error || ''}>error</span>}
    </>
  );
  if (!hayDetalle) return <div className={`aa-mov ${e.ok ? '' : 'aa-mov--error'}`}>{fila}<span className="aa-chevron aa-chevron--vacio" aria-hidden="true" /></div>;
  return (
    <details className={`aa-mov-det ${e.ok ? '' : 'aa-mov--error'}`}>
      <summary className="aa-mov">{fila}<span className="aa-chevron" aria-hidden="true" /></summary>
      <div className="aa-mov-extra">
        {personas && <span className="aa-par"><span>personas</span><b>{personas}</b></span>}
        {pares.map(([k, v]) => <span key={k} className="aa-par"><span>{k}</span><b>{String(v)}</b></span>)}
        {!e.ok && e.error && <span className="aa-par aa-par--error"><span>error</span><b>{e.error}</b></span>}
        {e.ip && <span className="aa-par"><span>IP</span><b>{e.ip}</b></span>}
        <span className="aa-par"><span>fecha</span><b>{fecha(e.fecha)}</b></span>
      </div>
    </details>
  );
}

function Registro({ admins, actor, setActor, categoria, setCategoria }) {
  const [pagina, setPagina] = useState(1);
  const { data, loading, error } = useQuery(GET_REGISTRO, {
    variables: { pagina, porPagina: POR_PAGINA, actor: actor || null, categoria: categoria || null },
    fetchPolicy: 'network-only',
  });
  const r = data?.getRegistroActividad;
  const grupos = useMemo(() => porDia(r ? r.items : []), [r]);
  return (
    <div className="aa-grupos">
      <div className="aa-filtros">
        <select value={actor} onChange={(e) => { setActor(e.target.value); setPagina(1); }} aria-label="Filtrar por admin">
          <option value="">Todos los admins</option>
          {admins.map((a) => <option key={a.email} value={a.email}>{a.email}</option>)}
        </select>
        <select value={categoria} onChange={(e) => { setCategoria(e.target.value); setPagina(1); }} aria-label="Filtrar por acción">
          {CATEGORIAS.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
        </select>
      </div>
      {loading && !r && <div className="spinner" />}
      {error && <div className="aa-aviso">{error.message}</div>}
      {r && r.total === 0 && <div className="aa-vacio">Sin acciones registradas con estos filtros.</div>}
      {r && r.total > 0 && (
        <>
          {grupos.map((g, i) => (
            <Grupo key={`${pagina}-${g.nombre}`} titulo={g.nombre} cantidad={g.items.length} abierto={i === 0}>
              {g.items.map((e) => <Movimiento key={e.id} e={e} />)}
            </Grupo>
          ))}
          <div className="aa-paginador">
            <button className="aa-boton" disabled={r.pagina <= 1} onClick={() => setPagina(r.pagina - 1)}>‹ Anterior</button>
            <span>Página {r.pagina} de {r.paginas} · {num(r.total)} acciones</span>
            <button className="aa-boton" disabled={r.pagina >= r.paginas} onClick={() => setPagina(r.pagina + 1)}>Siguiente ›</button>
          </div>
        </>
      )}
    </div>
  );
}

export default function ActividadAdmins() {
  const [dias, setDias] = useState(30);
  const [vista, setVista] = useState('warnings');
  const [actor, setActor] = useState('');
  const [categoria, setCategoria] = useState('');
  const { data, loading, error, refetch } = useQuery(GET_ACTIVIDAD, { variables: { dias }, fetchPolicy: 'network-only' });
  const r = data?.getActividadAdmins;
  const s = r?.resumen;
  const sinRegistro = s && !s.registroDesde;
  // Una tarjeta de arriba lleva a la pestaña (y al filtro del registro) que la explica
  const ir = (v, cat) => { setVista(v); if (cat !== undefined) { setCategoria(cat); setActor(''); } };

  const vistas = r && !r.error ? [
    ['warnings', 'Warnings', r.warnings.length],
    ['admins', 'Por admin', r.admins.length],
    ['altas', 'Altas', r.ultimasAltas.length],
    ['registro', 'Registro', null],
  ] : [];

  return (
    <div className="aa">
      <div className="aa-barra">
        <div className="aa-periodos" role="radiogroup" aria-label="Período">
          {PERIODOS.map(([v, t]) => (
            <button key={v} type="button" role="radio" aria-checked={dias === v} className={`aa-periodo ${dias === v ? 'aa-periodo--activo' : ''}`} onClick={() => setDias(v)}>{t}</button>
          ))}
        </div>
        <button className="aa-boton aa-boton--principal" disabled={loading} onClick={() => refetch()}>{loading ? 'Actualizando…' : 'Actualizar'}</button>
        {s && (
          <span className="aa-nota">
            {s.registroDesde
              ? `Registro detallado desde el ${soloDia(s.registroDesde)}. Las altas de cuentas y las personas salen de las cuentas, desde siempre.`
              : 'El registro detallado empieza con este despliegue. Por ahora se ven las altas de cuentas y las personas, que salen de las cuentas.'}
          </span>
        )}
      </div>

      {loading && !r && <div className="spinner" />}
      {error && <div className="aa-aviso">{error.message}</div>}
      {r?.error && <div className="aa-aviso">{r.errorMensaje}</div>}

      {r && !r.error && (
        <>
          <div className="aa-kpis">
            <Kpi titulo="Cuentas dadas de alta" valor={num(s.altasCuentas)} pie="Cuentas empresa y sin dashboard" onClick={() => ir('altas')} />
            <Kpi titulo="Usuarios de Office" valor={num(s.altasAdmins)} pie="Admins y superadmins dados de alta" onClick={() => ir('registro', 'alta_admin')} />
            <Kpi titulo="Personas agregadas" valor={num(s.personasAgregadas)} pie="Personas nuevas en los paneles" onClick={() => ir('admins')} />
            <Kpi titulo="Inicios de sesión" valor={num(s.logins)} pie={sinRegistro ? 'Se registran desde este despliegue' : `${num(s.loginsFallidos)} ${s.loginsFallidos === 1 ? 'fallido' : 'fallidos'}`} alerta={s.loginsFallidos > 0} onClick={() => ir('registro', 'login')} />
            <Kpi titulo="Roles y accesos" valor={num(s.cambiosRol)} pie={sinRegistro ? 'Se registran desde este despliegue' : 'Roles asignados y accesos a Office'} onClick={() => ir('registro', 'rol')} />
            <Kpi titulo="Warnings" valor={num(s.warnings)} pie="Altas y roles asignados" alerta={s.warnings > 0} onClick={() => ir('warnings')} />
          </div>

          <div className="aa-panel">
            <div className="aa-vistas" role="tablist" aria-label="Movimientos">
              {vistas.map(([k, t, n]) => (
                <button key={k} role="tab" aria-selected={vista === k} className={`aa-vista ${vista === k ? 'aa-vista--activa' : ''} ${k === 'warnings' && n > 0 ? 'aa-vista--alerta' : ''}`} onClick={() => setVista(k)}>
                  {t}{n != null && <span className="aa-vista-n">{num(n)}</span>}
                </button>
              ))}
            </div>
            <div className="aa-panel-cuerpo" role="tabpanel">
              {vista === 'warnings' && <Warnings lista={r.warnings} />}
              {vista === 'admins' && <PorAdmin admins={r.admins} sinRegistro={sinRegistro} />}
              {vista === 'altas' && <Altas lista={r.ultimasAltas} />}
              {vista === 'registro' && <Registro admins={r.admins} actor={actor} setActor={setActor} categoria={categoria} setCategoria={setCategoria} />}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
